import { signal, computed, onMount, Show } from "zyra";
import { pages, type Page } from "../content";
import type { SiteState } from "../app/site-state";
export function SearchDialog(props: { site: SiteState }) {
  const { searching, go } = props.site;
  const query = signal("");
  let input!: HTMLInputElement;
  let close!: HTMLButtonElement;
  const previous = document.activeElement as HTMLElement | null;
  const results = computed(() => {
    const phrase = query.value.toLowerCase().trim();
    const words = phrase.split(/\s+/);
    const rank = (page: Page) =>
      page.title.toLowerCase() === phrase
        ? 3
        : page.title.toLowerCase().includes(phrase)
          ? 2
          : page.description.toLowerCase().includes(phrase)
            ? 1
            : 0;
    return pages
      .filter((page) =>
        words.every((word) =>
          (page.title + " " + page.description + " " + page.body)
            .toLowerCase()
            .includes(word),
        ),
      )
      .sort((a, b) => rank(b) - rank(a));
  });
  onMount(() => {
    input.focus();
    const prior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const background = [
      ...document.querySelectorAll<HTMLElement>(".header,.site-grid,.landing-page"),
    ];
    background.forEach((node) => {
      node.inert = true;
    });
    return () => {
      document.body.style.overflow = prior;
      background.forEach((node) => {
        node.inert = false;
      });
      previous?.focus();
    };
  });
  const trap = (event: KeyboardEvent) => {
    if (event.defaultPrevented) return;
    const buttons = [
      ...document.querySelectorAll<HTMLButtonElement>(".search-result"),
    ];
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const index = buttons.indexOf(
        document.activeElement as HTMLButtonElement,
      );
      const next =
        event.key === "ArrowDown"
          ? (index + 1) % buttons.length
          : index <= 0
            ? buttons.length - 1
            : index - 1;
      buttons[next]?.focus();
    }
    if (event.key === "Tab") {
      const last = buttons.at(-1) ?? close;
      if (event.shiftKey && document.activeElement === input) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        input.focus();
      }
    }
  };
  return (
    <div
      class="search-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) searching.value = false;
      }}
    >
      <section
        class="search-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-title"
        onKeyDown={trap}
      >
        <h2 id="search-title" class="sr-only">
          Search documentation
        </h2>
        <div class="search-field">
          <span aria-hidden="true">⌕</span>
          <input
            ref={(el) => {
              input = el;
            }}
            type="search"
            placeholder="Search the documentation…"
            aria-label="Search documentation"
            value={query.value}
            onInput={(event) => {
              query.value = event.currentTarget.value;
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                document
                  .querySelector<HTMLButtonElement>(".search-result")
                  ?.focus();
              }
              if (event.key === "Enter" && results.value[0])
                go(results.value[0]);
            }}
          />
          <button
            ref={(el) => {
              close = el;
            }}
            title="Close search (Escape)"
            onClick={() => {
              searching.value = false;
            }}
          >
            Esc
          </button>
        </div>
        <div class="search-results" data-zyra-scroll-prevent>
          <span class="search-caption">
            {query.value
              ? results.value.length + " RESULTS"
              : "EXPLORE THE DOCS"}
          </span>
          {() =>
            results.value.map((page) => (
              <button class="search-result" onClick={() => go(page)}>
                <span class="search-result-icon">↗</span>
                <span>
                  <strong>{page.title}</strong>
                  <small>{page.description}</small>
                </span>
                <span>↵</span>
              </button>
            ))
          }
          <Show when={!results.value.length}>
            <p class="empty-search">
              No pages found. Try “signals”, “motion”, or “sync”.
            </p>
          </Show>
        </div>
        <div class="search-footer">
          Search titles, guides and API examples
          <span>↑↓ use Tab to browse · ↵ open</span>
        </div>
      </section>
    </div>
  );
}
